import { ParseMapError } from "../../errors";

export const CHALLENGE_WEEK_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const CHALLENGE_MAP_ID_RE = /^challenge\/([1-9]\d*)$/;

export const FIRST_CHALLENGE_MONDAY = "2026-11-02";

const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;

function utcDateFromDayString(day: string): Date {
  return new Date(`${day}T12:00:00.000Z`);
}

function toUtcDayString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function isChallengeWeekKey(weekKey: string): boolean {
  if (!CHALLENGE_WEEK_KEY_RE.test(weekKey)) return false;
  const date = utcDateFromDayString(weekKey);
  if (Number.isNaN(date.getTime()) || toUtcDayString(date) !== weekKey) return false;
  return date.getUTCDay() === 1;
}

export function isChallengeNumber(value: number): boolean {
  return Number.isInteger(value) && value >= 1;
}

export function parseChallengeNumber(raw: string): number | null {
  if (!/^[1-9]\d*$/.test(raw)) return null;
  const n = Number(raw);
  return isChallengeNumber(n) ? n : null;
}

export function isChallengeMapId(mapId: string): boolean {
  const match = CHALLENGE_MAP_ID_RE.exec(mapId);
  if (!match) return false;
  return parseChallengeNumber(match[1]) !== null;
}

export function challengeWeekKeyFromDateString(day: string): string {
  if (!CHALLENGE_WEEK_KEY_RE.test(day)) {
    throw new ParseMapError(`Invalid date string (expected YYYY-MM-DD): ${day}`);
  }
  const date = utcDateFromDayString(day);
  if (Number.isNaN(date.getTime()) || toUtcDayString(date) !== day) {
    throw new ParseMapError(`Invalid date string (expected YYYY-MM-DD): ${day}`);
  }
  const dayOfWeek = date.getUTCDay();
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  date.setUTCDate(date.getUTCDate() - daysFromMonday);
  return toUtcDayString(date);
}

export function currentChallengeWeekKey(now = new Date()): string {
  return challengeWeekKeyFromDateString(toUtcDayString(now));
}

export function previousChallengeWeekKey(now = new Date()): string {
  const current = utcDateFromDayString(currentChallengeWeekKey(now));
  current.setUTCDate(current.getUTCDate() - 7);
  return toUtcDayString(current);
}

export function nextChallengeWeekKey(weekKey: string): string {
  if (!isChallengeWeekKey(weekKey)) {
    throw new ParseMapError(`Invalid challenge week key (expected Monday YYYY-MM-DD): ${weekKey}`);
  }
  const monday = utcDateFromDayString(weekKey);
  monday.setUTCDate(monday.getUTCDate() + 7);
  return toUtcDayString(monday);
}

export function challengeNumberFromWeekKey(weekKey: string): number {
  if (!isChallengeWeekKey(weekKey)) {
    throw new ParseMapError(`Invalid challenge week key (expected Monday YYYY-MM-DD): ${weekKey}`);
  }
  if (weekKey < FIRST_CHALLENGE_MONDAY) {
    throw new ParseMapError(`Challenge week before FIRST_CHALLENGE_MONDAY: ${weekKey}`);
  }
  const first = utcDateFromDayString(FIRST_CHALLENGE_MONDAY).getTime();
  const week = utcDateFromDayString(weekKey).getTime();
  return Math.round((week - first) / WEEK_MS) + 1;
}

export function challengeWeekKeyFromNumber(challengeNumber: number): string {
  if (!isChallengeNumber(challengeNumber)) {
    throw new ParseMapError(`Invalid challenge number (expected integer >= 1): ${challengeNumber}`);
  }
  const first = utcDateFromDayString(FIRST_CHALLENGE_MONDAY);
  first.setUTCDate(first.getUTCDate() + (challengeNumber - 1) * 7);
  return toUtcDayString(first);
}

export function currentChallengeNumber(now = new Date()): number {
  return challengeNumberFromWeekKey(currentChallengeWeekKey(now));
}

export function previousChallengeNumber(now = new Date()): number | null {
  const previous = previousChallengeWeekKey(now);
  if (previous < FIRST_CHALLENGE_MONDAY) return null;
  return challengeNumberFromWeekKey(previous);
}

export function challengeNumberToMapId(challengeNumber: number): string {
  if (!isChallengeNumber(challengeNumber)) {
    throw new ParseMapError(`Invalid challenge number (expected integer >= 1): ${challengeNumber}`);
  }
  return `challenge/${challengeNumber}`;
}

export function challengeMapIdToNumber(mapId: string): number {
  const match = CHALLENGE_MAP_ID_RE.exec(mapId);
  const n = match ? parseChallengeNumber(match[1]) : null;
  if (n === null) {
    throw new ParseMapError(`Invalid challenge mapId (expected challenge/<N>): ${mapId}`);
  }
  return n;
}

export function challengeWeekKeyToMapId(weekKey: string): string {
  return challengeNumberToMapId(challengeNumberFromWeekKey(weekKey));
}

export function challengeMapIdToWeekKey(mapId: string): string {
  return challengeWeekKeyFromNumber(challengeMapIdToNumber(mapId));
}

export function challengeMapIdToS3Key(mapId: string): string {
  if (!isChallengeMapId(mapId)) {
    throw new ParseMapError(`Invalid challenge mapId (expected challenge/<N>): ${mapId}`);
  }
  return `${mapId}.json`;
}

export function isCurrentChallengeWeek(weekKey: string, now = new Date()): boolean {
  return isChallengeWeekKey(weekKey) && weekKey === currentChallengeWeekKey(now);
}

export function isCurrentChallengeNumber(challengeNumber: number, now = new Date()): boolean {
  return isChallengeNumber(challengeNumber) && challengeNumber === currentChallengeNumber(now);
}

export function isChallengeWeekOpen(weekKey: string, now = new Date()): boolean {
  if (!isChallengeWeekKey(weekKey)) return false;
  const today = toUtcDayString(now);
  return today >= weekKey && today < nextChallengeWeekKey(weekKey);
}

export function isChallengeNumberOpen(challengeNumber: number, now = new Date()): boolean {
  if (!isChallengeNumber(challengeNumber)) return false;
  return isChallengeWeekOpen(challengeWeekKeyFromNumber(challengeNumber), now);
}

export function isBrowsableChallengeLeaderboardNumber(
  challengeNumber: number,
  now = new Date(),
): boolean {
  if (!isChallengeNumber(challengeNumber)) return false;
  const current = currentChallengeNumber(now);
  if (challengeNumber === current) return true;
  const previous = previousChallengeNumber(now);
  return previous !== null && challengeNumber === previous;
}

export function isChallengeReplayPublic(challengeNumber: number, now = new Date()): boolean {
  if (!isChallengeNumber(challengeNumber)) return false;
  const previous = previousChallengeNumber(now);
  return previous !== null && challengeNumber === previous;
}

export function challengeNumber(weekKey: string): number {
  return challengeNumberFromWeekKey(weekKey);
}

export function challengePublicTitle(challengeNumber: number): string {
  if (!isChallengeNumber(challengeNumber)) {
    throw new ParseMapError(`Invalid challenge number (expected integer >= 1): ${challengeNumber}`);
  }
  return `Weekly Challenge #${challengeNumber}`;
}

export function challengeOgCaption(challengeNumber: number): string {
  if (!isChallengeNumber(challengeNumber)) {
    throw new ParseMapError(`Invalid challenge number (expected integer >= 1): ${challengeNumber}`);
  }
  return `Challenge Map #${challengeNumber}`;
}

export function isoWeekNumberFromMonday(weekKey: string): number {
  if (!isChallengeWeekKey(weekKey)) {
    throw new ParseMapError(`Invalid challenge week key (expected Monday YYYY-MM-DD): ${weekKey}`);
  }
  const monday = utcDateFromDayString(weekKey);
  const thursday = new Date(monday);
  thursday.setUTCDate(monday.getUTCDate() + 3);
  const yearStart = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 1));
  return Math.ceil(((thursday.getTime() - yearStart.getTime()) / DAY_MS + 1) / 7);
}
